import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Progress,
  Badge,
  Icon,
  Container,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  FormControl,
  FormLabel,
  Input,
  useToast,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import {
  FaFlask,
  FaUpload,
  FaChartLine,
  FaUsers,
  FaCheckCircle,
  FaArrowRight,
  FaArrowLeft,
  FaPlay,
  FaFileAlt,
  FaMicroscope,
  FaClipboardList,
  FaEdit,
  FaSave,
  FaTimes,
  FaCog
} from 'react-icons/fa';

const OnboardingGuide = ({ onComplete, labName = "Your Laboratory" }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [neverShowAgain, setNeverShowAgain] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const [customSteps, setCustomSteps] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load custom steps from localStorage on component mount
  React.useEffect(() => {
    const saved = localStorage.getItem(`onboarding-custom-steps-${labName}`);
    if (saved) {
      try {
        setCustomSteps(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load custom steps:', e);
      }
    }
  }, [labName]);

  // Save custom steps to localStorage
  const saveCustomSteps = (steps) => {
    setCustomSteps(steps);
    localStorage.setItem(`onboarding-custom-steps-${labName}`, JSON.stringify(steps));
  };

  // Handle editing a step
  const handleEditStep = (stepIndex) => {
    const step = allSteps[stepIndex];
    setEditingStepIndex(stepIndex);
    setEditForm({
      title: step.title,
      content: typeof step.content === 'string' ? step.content : 'Custom content'
    });
    onOpen();
  };

  // Save edited step
  const handleSaveEdit = () => {
    const newCustomSteps = [...customSteps];
    const customIndex = editingStepIndex;
    
    newCustomSteps[customIndex] = {
      ...allSteps[editingStepIndex],
      title: editForm.title,
      content: editForm.content,
      isCustom: true
    };
    
    saveCustomSteps(newCustomSteps);
    setIsEditing(false);
    setEditingStepIndex(null);
    onClose();
    
    toast({
      title: 'Step Updated',
      description: 'Your changes have been saved.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const steps = [
    {
      title: "Welcome to Your Lab Portal",
      icon: FaPlay,
      content: (
        <VStack spacing={6} align="start">
          <Alert status="success" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Welcome to {labName}!</AlertTitle>
              <AlertDescription>
                Your portal is now active and ready to streamline your laboratory operations.
              </AlertDescription>
            </Box>
          </Alert>

          <Box>
            <Heading size="md" mb={3}>What You'll Learn</Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                How to manage inbound blood samples and upload results
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                How to upload and manage your test directory
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                How to track revenue and monitor lab performance
              </ListItem>
              <ListItem>
                <ListIcon as={FaCheckCircle} color="green.500" />
                How to access support and resources
              </ListItem>
            </List>
          </Box>

          <Box>
            <Text fontSize="sm" color="gray.600">
              This guide will take about 3-5 minutes to complete and will help you get the most out of your portal.
            </Text>
          </Box>
        </VStack>
      )
    },
    {
      title: "Dashboard: Inbound Blood Flow",
      icon: FaFlask,
      content: (
        <VStack spacing={6} align="start">
          <Box>
            <Heading size="md" mb={3}>Your Primary Workspace</Heading>
            <Text mb={4}>
              The dashboard is your command center for managing blood samples and results.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
            <Card size="sm" bg={bgColor} border="1px" borderColor={borderColor}>
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaMicroscope} color="blue.500" />
                  <Text fontWeight="bold" fontSize="sm">Incoming Samples</Text>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                <Text fontSize="xs" color="gray.600">
                  View blood samples on their way to your lab with collection details and priority status.
                </Text>
              </CardBody>
            </Card>

            <Card size="sm" bg={bgColor} border="1px" borderColor={borderColor}>
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaUpload} color="green.500" />
                  <Text fontWeight="bold" fontSize="sm">Upload Results</Text>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                <Text fontSize="xs" color="gray.600">
                  Upload test results directly to the portal for immediate patient and provider access.
                </Text>
              </CardBody>
            </Card>

            <Card size="sm" bg={bgColor} border="1px" borderColor={borderColor}>
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaChartLine} color="purple.500" />
                  <Text fontWeight="bold" fontSize="sm">Performance Metrics</Text>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                <Text fontSize="xs" color="gray.600">
                  Track turnaround times, sample volumes, and revenue analytics.
                </Text>
              </CardBody>
            </Card>

            <Card size="sm" bg={bgColor} border="1px" borderColor={borderColor}>
              <CardHeader pb={2}>
                <HStack>
                  <Icon as={FaUsers} color="orange.500" />
                  <Text fontWeight="bold" fontSize="sm">Patient Communication</Text>
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                <Text fontSize="xs" color="gray.600">
                  Automated notifications keep patients informed about their results status.
                </Text>
              </CardBody>
            </Card>
          </SimpleGrid>

          <Alert status="info" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertDescription>
                <strong>Pro Tip:</strong> The dashboard updates in real-time, so you'll always see the latest sample status and can respond quickly to urgent requests.
              </AlertDescription>
            </Box>
          </Alert>
        </VStack>
      )
    },
    {
      title: "Test Directory Management",
      icon: FaClipboardList,
      content: (
        <VStack spacing={6} align="start">
          <Box>
            <Heading size="md" mb={3}>Showcase Your Services</Heading>
            <Text mb={4}>
              Your test directory is your public storefront - it's how patients and providers discover your services.
            </Text>
          </Box>

          <Box w="full">
            <Heading size="sm" mb={3}>Getting Started with Test Directory</Heading>
            <List spacing={3}>
              <ListItem>
                <HStack align="start">
                  <Icon as={FaFileAlt} color="blue.500" mt={1} />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Upload Your Test Menu</Text>
                    <Text fontSize="xs" color="gray.600">
                      Upload your test menu in any format (PDF, Excel, CSV, Word, etc.). We'll review and format it properly.
                    </Text>
                  </Box>
                </HStack>
              </ListItem>
              <ListItem>
                <HStack align="start">
                  <Icon as={FaChartLine} color="green.500" mt={1} />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Set Pricing</Text>
                    <Text fontSize="xs" color="gray.600">
                      Add cash prices for direct-pay patients and special promotional pricing.
                    </Text>
                  </Box>
                </HStack>
              </ListItem>
              <ListItem>
                <HStack align="start">
                  <Icon as={FaUsers} color="purple.500" mt={1} />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Public Visibility</Text>
                    <Text fontSize="xs" color="gray.600">
                      Your test directory will be visible to patients searching for specific tests in your area.
                    </Text>
                  </Box>
                </HStack>
              </ListItem>
            </List>
          </Box>

          <Alert status="warning" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Universal File Upload Support</AlertTitle>
              <AlertDescription>
                Don't worry about perfect formatting! Upload your test menu in any format (PDF, Excel, CSV, Word, Images, etc.), and our team will review and organize it properly for optimal visibility and searchability.
              </AlertDescription>
            </Box>
          </Alert>
        </VStack>
      )
    },
    {
      title: "You're All Set!",
      icon: FaCheckCircle,
      content: (
        <VStack spacing={6} align="start">
          <Alert status="success" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Portal Setup Complete!</AlertTitle>
              <AlertDescription>
                {labName} is now fully integrated into our network and ready to receive samples.
              </AlertDescription>
            </Box>
          </Alert>

          <Box>
            <Heading size="md" mb={3}>Next Steps</Heading>
            <List spacing={3}>
              <ListItem>
                <HStack>
                  <Badge colorScheme="blue" variant="solid">1</Badge>
                  <Text fontSize="sm">Upload your test directory to start attracting patients</Text>
                </HStack>
              </ListItem>
              <ListItem>
                <HStack>
                  <Badge colorScheme="green" variant="solid">2</Badge>
                  <Text fontSize="sm">Familiarize yourself with the dashboard layout</Text>
                </HStack>
              </ListItem>
              <ListItem>
                <HStack>
                  <Badge colorScheme="purple" variant="solid">3</Badge>
                  <Text fontSize="sm">Set up result upload procedures with your lab team</Text>
                </HStack>
              </ListItem>
            </List>
          </Box>
        </VStack>
      )
    }
  ];

  // Merge default steps with custom steps
  const allSteps = customSteps.length > 0 ? customSteps : steps;

  const handleNext = () => {
    if (currentStep < allSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as completed for this lab
      localStorage.setItem(`onboarding-completed-${labName}`, 'true');
      
      // Save "never show again" preference if checked
      if (neverShowAgain) {
        localStorage.setItem(`onboarding-never-show-${labName}`, 'true');
      }
      
      onComplete?.();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipOnboarding = () => {
    // Mark onboarding as completed (even if skipped)
    localStorage.setItem(`onboarding-completed-${labName}`, 'true');
    
    // Save "never show again" preference if checked
    if (neverShowAgain) {
      localStorage.setItem(`onboarding-never-show-${labName}`, 'true');
    }
    
    onComplete?.();
  };

  const progress = ((currentStep + 1) / allSteps.length) * 100;

  return (
    <Container maxW="4xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Progress Header with Controls */}
        <Box>
          <HStack justify="space-between" mb={2}>
            <HStack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Step {currentStep + 1} of {allSteps.length}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {Math.round(progress)}% Complete
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Tooltip label="Edit this step">
                <IconButton
                  size="sm"
                  icon={<FaEdit />}
                  onClick={() => handleEditStep(currentStep)}
                  variant="outline"
                  colorScheme="blue"
                />
              </Tooltip>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleSkipOnboarding}
                leftIcon={<FaTimes />}
              >
                Skip Guide
              </Button>
            </HStack>
          </HStack>
          <Progress value={progress} colorScheme="blue" size="sm" borderRadius="full" />
        </Box>

        {/* Step Content */}
        <Card size="lg" bg={bgColor}>
          <CardHeader>
            <HStack spacing={4} justify="space-between">
              <HStack spacing={4}>
                <Icon 
                  as={allSteps[currentStep].icon} 
                  boxSize={8} 
                  color="blue.500" 
                />
                <Heading size="lg" color="blue.600">
                  {allSteps[currentStep].title}
                </Heading>
              </HStack>
              {allSteps[currentStep].isCustom && (
                <Badge colorScheme="green" variant="solid">
                  <Icon as={FaEdit} mr={1} />
                  Customized
                </Badge>
              )}
            </HStack>
          </CardHeader>
          <CardBody>
            {typeof allSteps[currentStep].content === 'string' ? (
              <Text whiteSpace="pre-line">{allSteps[currentStep].content}</Text>
            ) : (
              allSteps[currentStep].content
            )}
          </CardBody>
        </Card>

        {/* Navigation */}
        <VStack spacing={4}>
          {/* Never Show Again Checkbox (only on last step) */}
          {currentStep === allSteps.length - 1 && (
            <HStack justify="center">
              <Checkbox
                isChecked={neverShowAgain}
                onChange={(e) => setNeverShowAgain(e.target.checked)}
                colorScheme="blue"
              >
                <Text fontSize="sm" color="gray.600">
                  Don't show this onboarding guide again for {labName}
                </Text>
              </Checkbox>
            </HStack>
          )}

          <HStack justify="space-between" w="full">
            <Button
              leftIcon={<FaArrowLeft />}
              variant="outline"
              onClick={handlePrevious}
              isDisabled={currentStep === 0}
              size="lg"
            >
              Previous
            </Button>

            <Button
              rightIcon={currentStep === allSteps.length - 1 ? <FaCheckCircle /> : <FaArrowRight />}
              colorScheme="blue"
              onClick={handleNext}
              size="lg"
            >
              {currentStep === allSteps.length - 1 ? 'Complete Setup' : 'Next'}
            </Button>
          </HStack>
        </VStack>
      </VStack>

      {/* Edit Step Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={FaCog} color="blue.500" />
              <Text>Edit Onboarding Step</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Step Title</FormLabel>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Enter step title"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Step Content</FormLabel>
                <Textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  placeholder="Enter step content (supports line breaks)"
                  rows={8}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Tip: Use line breaks to separate paragraphs. You can include instructions, tips, or any custom content for your team.
                </Text>
              </FormControl>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">Customization Note</AlertTitle>
                  <AlertDescription fontSize="xs">
                    Your changes will be saved locally for this lab. The original guide remains unchanged for other labs.
                  </AlertDescription>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveEdit}
              leftIcon={<FaSave />}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default OnboardingGuide;

